// DepEd SY 2026-2027 transition grading reference: the passing mark, the four
// intervention tiers and their descriptors.
//
// Extracted here from competencyMap.ts so both the competency layer and the
// item/quiz layer read ONE copy of the table. competencyMap.ts re-exports these,
// so existing imports are unchanged. The split also keeps the pure statistical
// modules free of competencyMap.ts's CSV-parser import.
//
// The reference table is specified for COMPETENCY-level percentages. Applying it
// to an overall quiz percentage (see scoreBands in itemAnalysis.ts) is a
// deliberate extension of the same table and must be stated wherever it is shown.

/** Below this many items, one learner's percentage on a competency is noise
 *  (2 items can only read 0/50/100), so per-learner mastery is not asserted. */
export const MIN_ITEMS_FOR_LEARNER = 3;

/** DepEd's passing mark, and the operative mastery cut-off. Not configurable:
 *  the instructional-response bands below are pinned to absolute score ranges
 *  around it, so a custom cut-off would silently invalidate their labels. */
export const MASTERY_CUTOFF = 75;

export type InterventionTier = 'none' | 'guided' | 'targeted' | 'sustained';

export interface Intervention {
  tier: InterventionTier;
  /** DepEd descriptor for the band, '' where the reference table wasn't supplied. */
  descriptor: string;
  /** what the teacher is meant to do for ONE learner at this band. */
  response: string;
  /**
   * The same band applied to a class average. Deliberately worded differently:
   * a class can average 82% while individual learners sit below the passing mark,
   * so the learner sentence ("no remediation needed") would contradict the
   * remediation roster printed beside it.
   */
  classResponse: string;
  /** true below the 75 passing mark — i.e. belongs in a remediation list. */
  needsRemediation: boolean;
}

/**
 * Map a percentage to DepEd's instructional response (SY 2026-2027 transition
 * grading reference). 75 is the passing mark: at 75-79 the learner passed and
 * needs guided practice for consistency, NOT remediation — that distinction is
 * the point of this table, so 75-79 is reported separately from the remediation
 * list rather than lumped in with it.
 *
 * The supplied reference covers 75-79, 65-74 and 0-64. Bands at 80+ are passing
 * and need no instructional response, so they carry no descriptor here rather
 * than an invented one.
 */
export function interventionFor(pct: number): Intervention {
  if (pct >= 80) {
    return {
      tier: 'none',
      descriptor: '',
      response: 'Passing — no remediation needed',
      classResponse: 'Class average is passing — no class-wide remediation',
      needsRemediation: false,
    };
  }
  if (pct >= MASTERY_CUTOFF) {
    return {
      tier: 'guided',
      descriptor: 'Connecting / Natutungo',
      response: 'Passing — guided practice for consistency',
      classResponse: 'Class average is passing — guided practice for consistency',
      needsRemediation: false,
    };
  }
  if (pct >= 65) {
    return {
      tier: 'targeted',
      descriptor: 'Developing / Napauunlad',
      response: 'Targeted remediation; scaffolding',
      classResponse: 'Class-wide targeted remediation; scaffolding',
      needsRemediation: true,
    };
  }
  return {
    tier: 'sustained',
    descriptor: 'Emerging / Nagsisimula',
    response: 'Structured, sustained intervention',
    classResponse: 'Class-wide structured, sustained intervention',
    needsRemediation: true,
  };
}

/**
 * The same four bands as a binning table, highest first — the ONLY score bins in
 * this app. Deciles/quartiles would be a fabricated threshold; these cut-offs are
 * the reference table's own. `min`/`max` are inclusive whole percentages, so a
 * percentage must be rounded before it is compared (matching the rule that the
 * number shown is the number the decision used).
 *
 * Descriptors are read back out of interventionFor rather than re-typed, so the
 * strings can never drift from the instructional-response table above.
 */
export const DEPED_BANDS: ReadonlyArray<{
  tier: InterventionTier;
  min: number;
  max: number;
  /** "75–79" — the band as printed. */
  range: string;
  /** DepEd descriptor; '' for the 80+ band, which the reference table leaves unnamed. */
  descriptor: string;
}> = [
  { tier: 'none', min: 80, max: 100 },
  { tier: 'guided', min: MASTERY_CUTOFF, max: 79 },
  { tier: 'targeted', min: 65, max: 74 },
  { tier: 'sustained', min: 0, max: 64 },
].map((b) => ({ ...b, tier: b.tier as InterventionTier, range: `${b.min}–${b.max}`, descriptor: interventionFor(b.min).descriptor }));

/** The band a whole-number percentage falls in. Percentages must already be rounded. */
export function bandFor(pct: number): (typeof DEPED_BANDS)[number] {
  return DEPED_BANDS.find((b) => pct >= b.min) ?? DEPED_BANDS[DEPED_BANDS.length - 1];
}
